import {
	Button,
	ButtonUtility,
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxList,
	ComboboxTrigger,
	ComboboxValue,
	DatePicker,
	FormFieldControl,
	Input,
	Modal,
	ModalOverlay,
	Select,
	Spinner,
	ToastCustom,
	useComboboxAnchor,
} from "@components";
import { FileUpload } from "@components/file-upload";
import { zodResolver } from "@hookform/resolvers/zod";
import { CalendarDate } from "@internationalized/date";
import { useConfirmModal } from "@providers";
import { FileAttachment02, Plus, XClose } from "@untitledui/icons";
import { type Key, useEffect, useRef, useState } from "react";
import type { DateValue } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { TextAreaBase } from "@/components/base/textarea/textarea";
import { formatFuelLitersNumber, parseFuelLiters } from "@/shared/utils";
import { cx } from "@/utils/cx";
import {
	REIMBURSEMENT_REASON_OPTIONS,
	REPORT_SUMMARY_CATEGORY_NAMES,
} from "../constants";
import type { VehiclesLookupSelectItem } from "../handlers";
import {
	useCreateExpense,
	useGetExpenseById,
	useGetVehiclesLookup,
	useUpdateExpense,
} from "../hooks";
import { useOpenExpenseAttachment } from "../hooks/use-open-expense-attachment";
import { type NewExpenseSchema, newExpenseSchema } from "../schemas";
import { expensePresignedUrl } from "../services";
import {
	type CreateExpensePayloadApi,
	EXPENSE_TYPE_CODES,
	type ExpenseTypeCode,
	MAINTENANCE_TYPE_CODES,
	type MaintenanceTypeCode,
	type UpdateExpensePayloadApi,
} from "../types";

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB

/** Formata string digitada para exibição em pt-BR (ex: "123456" → "1.234,56"). */
export function formatCurrencyBR(value: string): string {
	const numbersOnly = value.replace(/\D/g, "");

	if (numbersOnly.length === 0) {
		return "";
	}

	const number = Number(numbersOnly) / 100;

	return number.toLocaleString("pt-BR", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
}

/** Converte número (estado do form) para string de exibição no input (ex: 1234.56 → "1.234,56"). */
function formatNumberToDisplayBR(value: number): string {
	return value.toLocaleString("pt-BR", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
}

/** Converte string do input (pt-BR) para número para o validador/API. */
function parseDisplayBRToNumber(value: string): number | undefined {
	const numbersOnly = value.replace(/\D/g, "");
	if (numbersOnly.length === 0) {
		return;
	}
	const num = Number(numbersOnly) / 100;
	return Number.isNaN(num) ? undefined : num;
}

function normalizePlateForSearch(value: string): string {
	return value.replace(/[^a-z0-9]/gi, "").toUpperCase();
}

export type ExpenseModalMode = "create" | "view" | "edit";

type NewExpenseModalProps = {
	isOpen: boolean;
	onClose: () => void;
	onSuccess?: () => void;
	mode?: ExpenseModalMode;
	id?: string | null;
};

const MAINTENANCE_OPTIONS: { value: MaintenanceTypeCode; label: string }[] = [
	{ value: MAINTENANCE_TYPE_CODES.Preventive, label: "Preventiva" },
	{ value: MAINTENANCE_TYPE_CODES.Corrective, label: "Corretiva" },
];

const defaultValues: Partial<NewExpenseSchema> = {
	type: undefined,
	vehicleId: "",
	amount: undefined,
	date: "",
	description: "",
	attachment: undefined,
	infractionNumber: "",
	infractionDueDate: "",
	driverIdentificationDueDate: "",
	maintenanceType: undefined,
	fuelLiters: undefined,
	reimbursementReason: undefined,
};

function stringToDateValue(str: string): CalendarDate | null {
	if (!str) {
		return null;
	}
	const [y, m, d] = str.split("-").map(Number);
	if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) {
		return null;
	}
	return new CalendarDate(y, m, d);
}

function dateValueToString(date: DateValue | null): string {
	if (!date) {
		return "";
	}
	const y = date.year;
	const m = String(date.month).padStart(2, "0");
	const d = String(date.day).padStart(2, "0");
	return `${y}-${m}-${d}`;
}

function buildCreatePayloadApi(
	values: NewExpenseSchema,
	fileKey?: string,
	fileName?: string
): CreateExpensePayloadApi {
	const payload: CreateExpensePayloadApi = {
		vehicleId: Number(values.vehicleId),
		type: values.type as ExpenseTypeCode,
		amount: values.amount,
		date: values.date,
		description: values.description?.trim() || undefined,
		fileName: fileName ?? undefined,
		fileKey: fileKey ?? undefined,
	};

	if (values.type === EXPENSE_TYPE_CODES.Infraction) {
		payload.infractionNumber = values.infractionNumber?.trim() || undefined;
		payload.infractionDueDate = values.infractionDueDate?.trim() || undefined;
		payload.driverIdentificationDueDate =
			values.driverIdentificationDueDate?.trim() || undefined;
	}
	if (
		values.type === EXPENSE_TYPE_CODES.Maintenance &&
		values.maintenanceType !== undefined &&
		values.maintenanceType !== null
	) {
		payload.maintenanceType = values.maintenanceType;
	}
	if (
		values.type === EXPENSE_TYPE_CODES.Fuel &&
		values.fuelLiters !== undefined &&
		values.fuelLiters !== null
	) {
		payload.fuelLiters = values.fuelLiters;
	}
	if (
		values.type === EXPENSE_TYPE_CODES.Reimbursement &&
		values.reimbursementReason !== undefined &&
		values.reimbursementReason !== null
	) {
		payload.reimbursementReason = values.reimbursementReason;
	}

	return payload;
}

function buildUpdatePayloadApi(
	values: NewExpenseSchema,
	fileKey?: string,
	fileName?: string
): UpdateExpensePayloadApi {
	const payload: UpdateExpensePayloadApi = {
		amount: values.amount,
		date: values.date,
		description: values.description?.trim() || undefined,
		fileName: fileName ?? undefined,
		fileKey: fileKey ?? undefined,
	};

	if (values.type === EXPENSE_TYPE_CODES.Infraction) {
		payload.infractionNumber = values.infractionNumber?.trim() || undefined;
		payload.infractionDueDate = values.infractionDueDate?.trim() || undefined;
		payload.driverIdentificationDueDate =
			values.driverIdentificationDueDate?.trim() || undefined;
	}
	if (
		values.type === EXPENSE_TYPE_CODES.Maintenance &&
		values.maintenanceType !== undefined &&
		values.maintenanceType !== null
	) {
		payload.maintenanceType = values.maintenanceType;
	}
	if (
		values.type === EXPENSE_TYPE_CODES.Fuel &&
		values.fuelLiters !== undefined &&
		values.fuelLiters !== null
	) {
		payload.fuelLiters = values.fuelLiters;
	}
	if (
		values.type === EXPENSE_TYPE_CODES.Reimbursement &&
		values.reimbursementReason !== undefined &&
		values.reimbursementReason !== null
	) {
		payload.reimbursementReason = values.reimbursementReason;
	}

	return payload;
}

export function NewExpenseModal({
	isOpen,
	onClose,
	onSuccess,
	mode = "create",
	id,
}: NewExpenseModalProps) {
	const anchor = useComboboxAnchor();
	const isConfirmModalOpenRef = useRef(false);

	const { data: expenseData, isLoading: isLoadingExpense } = useGetExpenseById(
		id ?? ""
	);

	const { mutate: openAttachment, isPending: isOpeningAttachment } =
		useOpenExpenseAttachment();

	const { data: vehiclesData, isLoading: isLoadingVehicles } =
		useGetVehiclesLookup(isOpen);

	const {
		control,
		handleSubmit,
		reset,
		watch,
		formState: { errors, isSubmitting, disabled },
	} = useForm<NewExpenseSchema>({
		resolver: zodResolver(newExpenseSchema),
		defaultValues,
		disabled: isLoadingExpense,
	});

	const isReadOnly = mode === "view";

	const { confirm } = useConfirmModal();

	const handleClose = () => {
		reset(defaultValues);
		onClose();
	};

	const [isFuelLitersFocused, setIsFuelLitersFocused] = useState(false);

	const createMutation = useCreateExpense(() => {
		onSuccess?.();
		handleClose();
	});
	const updateMutation = useUpdateExpense(() => {
		onSuccess?.();
		handleClose();
	});

	const type = watch("type");

	useEffect(() => {
		if (isOpen) {
			if (expenseData) {
				reset({
					type: expenseData.type,
					vehicleId: String(expenseData.vehicleId),
					amount: expenseData.amount,
					date: expenseData.date,
					description: expenseData.description ?? "",
					attachment: undefined,
					infractionNumber: expenseData.infractionNumber ?? "",
					infractionDueDate: expenseData.infractionDueDate ?? "",
					driverIdentificationDueDate:
						expenseData.driverIdentificationDueDate ?? "",
					maintenanceType: expenseData.maintenanceType ?? undefined,
					fuelLiters: expenseData.fuelLiters ?? undefined,
					reimbursementReason: expenseData.reimbursementReason ?? undefined,
				});
			} else {
				reset(defaultValues);
			}
		}
	}, [isOpen, expenseData, reset]);

	const confirmEditIfNeeded = async (file: File | null) => {
		isConfirmModalOpenRef.current = true;
		try {
			if (file) {
				const result = await confirm({
					title: "Tem certeza de que deseja substituir o anexo cadastrado?",
					description: "Ao substituí-lo, você não poderá recuperá-lo.",
					cancelText: "Cancelar",
					confirmText: "Salvar Edição de Anexo",
					variant: "warning",
				});
				if (!result) {
					return false;
				}
			} else {
				const result = await confirm({
					title: "Confirma a edição da despesa?",
					description: "Ao confirmar, a despesa é atualizada.",
					cancelText: "Cancelar",
					confirmText: "Confirmar Edição",
					variant: "warning",
				});
				if (!result) {
					return false;
				}
			}
			return true;
		} finally {
			// Evita que o fechamento do modal de confirmação dispare onOpenChange(false) no modal pai
			isConfirmModalOpenRef.current = false;
		}
	};

	const onSubmit = async (values: NewExpenseSchema) => {
		let fileKey: string | undefined;
		let fileName: string | undefined;
		const attachment = values.attachment;

		if (mode === "edit") {
			const canProceed = await confirmEditIfNeeded(attachment ?? null);
			if (!canProceed) {
				return;
			}
		}

		if (values.attachment instanceof File) {
			const result = await expensePresignedUrl({
				type: values.type,
				vehicleId: Number(values.vehicleId),
				fileName: values.attachment.name,
				contentType: values.attachment.type || "application/octet-stream",
				file: values.attachment,
			});
			fileKey = result.fileKey;
			fileName = result.fileName;
		}

		if (mode === "edit" && id) {
			const payload = buildUpdatePayloadApi(values, fileKey, fileName);
			await updateMutation.mutateAsync({
				expenseId: id,
				payload,
			});
		} else {
			const payload = buildCreatePayloadApi(values, fileKey, fileName);
			await createMutation.mutateAsync(payload);
		}

		onSuccess?.();
		handleClose();
	};

	const handleSubmitKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			handleSubmit(onSubmit)();
		}
	};

	const isPending = createMutation.isPending || updateMutation.isPending;

	return (
		<ModalOverlay
			className="pl-0"
			isOpen={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					handleClose();
				}
			}}
		>
			<Modal className="relative mx-auto flex h-fit max-h-[calc(100dvh-2rem)] max-w-[708px]! flex-col gap-0 overflow-hidden rounded-2xl bg-bg-primary p-0 max-sm:overflow-hidden sm:max-h-[calc(100vh-10rem)]">
				<div className="relative flex shrink-0 flex-row items-center justify-between gap-4 bg-bg-primary p-6 pb-4">
					<h1 className="font-semibold text-lg text-primary">
						{(() => {
							if (mode === "view") {
								return "Visualizar despesa";
							}
							if (mode === "edit") {
								return "Editar despesa";
							}
							return "Nova despesa";
						})()}
					</h1>

					<ButtonUtility
						className="absolute top-7 right-5 z-10"
						color="tertiary"
						icon={<XClose className="size-6 text-quaternary" />}
						onClick={handleClose}
						tooltip="Fechar"
					/>
				</div>

				<div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
					<form
						className="flex max-w-full flex-col gap-4 overflow-x-hidden p-6 pt-0"
						onSubmit={handleSubmit(onSubmit)}
					>
						<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
							<Controller
								control={control}
								name="type"
								render={({ field }) => {
									const items = Object.entries(
										REPORT_SUMMARY_CATEGORY_NAMES
									).map(([key, value]) => ({
										id: key,
										label: value,
									}));

									const onChange = (v: Key | null) => {
										if (v === "" || v === undefined) {
											field.onChange(undefined);
										} else {
											field.onChange(Number(v));
										}
									};
									return (
										<FormFieldControl
											error={errors.type?.message}
											htmlFor="expense-type"
											isRequired
											label="Tipo"
										>
											<Select
												aria-labelledby="expense-type"
												className="w-full"
												isDisabled={isReadOnly || mode === "edit"}
												items={items}
												onChange={onChange}
												placeholder="Selecione"
												value={field.value?.toString() ?? ""}
											>
												{(item) => (
													<Select.Item id={item.id}>{item.label}</Select.Item>
												)}
											</Select>
										</FormFieldControl>
									);
								}}
							/>

							<Controller
								control={control}
								name="vehicleId"
								render={({ field }) => (
									<FormFieldControl
										error={errors.vehicleId?.message}
										htmlFor="expense-vehicle"
										isRequired
										label="Veículo"
									>
										<div className="w-full" ref={anchor}>
											<Combobox
												autoHighlight
												disabled={field.disabled || mode !== "create"}
												filter={(
													itemValue: VehiclesLookupSelectItem,
													query: string
												) => {
													const normalizedQuery =
														normalizePlateForSearch(query);

													// Se a busca estiver vazia, mantém todos os itens visíveis.
													if (!normalizedQuery) {
														return true;
													}

													// A busca deve ignorar qualquer caractere que não seja
													// letra ou número (ex: "-", ".", espaço, etc).
													const normalizedItemPlate = normalizePlateForSearch(
														itemValue.label
													);

													return normalizedItemPlate.includes(normalizedQuery);
												}}
												items={vehiclesData}
												itemToStringValue={(
													vehicle: VehiclesLookupSelectItem
												) => vehicle.id}
												onValueChange={(fieldValue) => {
													field.onChange(fieldValue);
												}}
												value={
													vehiclesData?.find(
														(vehicle) => vehicle.id === field.value
													) ?? null
												}
											>
												<ComboboxTrigger
													render={
														<Button
															className="focus:primary w-full justify-start bg-primary px-3.5! font-medium text-md shadow-xs focus:shadow-button focus:ring-brand focus-visible:ring-0"
															disabled={isLoadingVehicles}
															size="lg"
															variant="outline"
														>
															{!!isLoadingVehicles && <Spinner disabled />}
															<span className="w-full max-w-full truncate">
																<ComboboxValue placeholder="Selecione a placa..." />
															</span>
														</Button>
													}
												/>
												<ComboboxContent
													anchor={anchor}
													className="w-72 bg-primary"
												>
													<ComboboxInput
														placeholder="Busca por veículo"
														showTrigger={false}
													/>
													<ComboboxEmpty>
														Nenhum veículo encontrado.
													</ComboboxEmpty>
													<ComboboxList>
														{(item) => (
															<ComboboxItem
																className="h-10 min-h-fit text-md"
																key={item.id}
																value={item.id}
															>
																{item.label}
															</ComboboxItem>
														)}
													</ComboboxList>
												</ComboboxContent>
											</Combobox>
										</div>
									</FormFieldControl>
								)}
							/>
						</div>

						<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
							<Controller
								control={control}
								name="amount"
								render={({ field }) => (
									<FormFieldControl
										error={errors.amount?.message}
										htmlFor="expense-amount"
										isRequired
										label="Valor (R$)"
									>
										<Input
											aria-label="Valor em reais"
											disabled={isReadOnly || field.disabled}
											id="expense-amount"
											maxLength={19}
											min={0}
											onChange={(e) => {
												const num = parseDisplayBRToNumber(e.target.value);
												field.onChange(num ?? undefined);
											}}
											placeholder="0,00"
											step="0.01"
											type="text"
											value={
												field.value !== undefined && field.value !== null
													? formatNumberToDisplayBR(field.value)
													: ""
											}
										/>
									</FormFieldControl>
								)}
							/>

							<Controller
								control={control}
								name="date"
								render={({ field }) => (
									<FormFieldControl
										error={errors.date?.message}
										htmlFor="expense-date"
										isRequired
										label="Data"
									>
										<DatePicker
											aria-describedby="expense-date"
											aria-description="Data da despesa"
											aria-label="Data da despesa"
											className="w-full"
											id="expense-date"
											isDisabled={isReadOnly || field.disabled}
											onChange={(date) => {
												field.onChange(dateValueToString(date));
											}}
											value={stringToDateValue(field.value ?? "")}
										/>
									</FormFieldControl>
								)}
							/>
						</div>

						{type === EXPENSE_TYPE_CODES.Infraction && (
							<div className="flex flex-col gap-3 border-border-secondary border-t pt-4">
								<p className="font-bold text-secondary text-xs uppercase">
									Campos de Infração
								</p>
								<Controller
									control={control}
									name="infractionNumber"
									render={({ field }) => (
										<FormFieldControl
											error={errors.infractionNumber?.message}
											htmlFor="expense-infraction-number"
											isRequired
											label="Código do auto"
										>
											<Input
												aria-label="Código do auto"
												disabled={isReadOnly || field.disabled}
												id="expense-infraction-number"
												maxLength={15}
												onChange={(e) => {
													const { value } = e.target;
													field.onChange(value?.toUpperCase()?.trim());
												}}
												placeholder="Ex: 123456789012345"
												value={field.value ?? ""}
											/>
										</FormFieldControl>
									)}
								/>
								<Controller
									control={control}
									name="infractionDueDate"
									render={({ field }) => (
										<FormFieldControl
											error={errors.infractionDueDate?.message}
											htmlFor="expense-infraction-due-date"
											label="Data de vencimento"
										>
											<DatePicker
												aria-label="Data de vencimento"
												className="w-full"
												id="expense-infraction-due-date"
												isDisabled={isReadOnly || field.disabled}
												onChange={(date) => {
													field.onChange(dateValueToString(date));
												}}
												value={stringToDateValue(field.value ?? "")}
											/>
										</FormFieldControl>
									)}
								/>
								<Controller
									control={control}
									name="driverIdentificationDueDate"
									render={({ field }) => (
										<FormFieldControl
											error={errors.driverIdentificationDueDate?.message}
											htmlFor="expense-driver-identification-due-date"
											isRequired
											label="Data limite identificação do condutor"
										>
											<DatePicker
												aria-label="Data limite identificação do condutor"
												className="w-full"
												id="expense-driver-identification-due-date"
												isDisabled={isReadOnly || field.disabled}
												onChange={(date) => {
													field.onChange(dateValueToString(date));
												}}
												value={stringToDateValue(field.value ?? "")}
											/>
										</FormFieldControl>
									)}
								/>
							</div>
						)}

						{type === EXPENSE_TYPE_CODES.Maintenance && (
							<div className="flex flex-col gap-3 border-border-secondary border-t pt-4">
								<p className="font-bold text-secondary text-xs uppercase">
									Campos de Manutenção
								</p>
								<Controller
									control={control}
									name="maintenanceType"
									render={({ field }) => (
										<FormFieldControl
											error={errors.maintenanceType?.message}
											htmlFor="expense-maintenance-type"
											isRequired
											label="Tipo de manutenção"
										>
											<Select
												aria-labelledby="expense-maintenance-type"
												className="w-full"
												isDisabled={isReadOnly || field.disabled}
												items={MAINTENANCE_OPTIONS.map((opt) => ({
													id: opt.value,
													label: opt.label,
												}))}
												onChange={(v) => {
													field.onChange(Number(v));
												}}
												placeholder="Selecione"
												value={field.value}
											>
												{(item) => (
													<Select.Item id={item.id}>{item.label}</Select.Item>
												)}
											</Select>
										</FormFieldControl>
									)}
								/>
							</div>
						)}

						{type === EXPENSE_TYPE_CODES.Fuel && (
							<div className="flex flex-col gap-3 border-border-secondary border-t pt-4">
								<p className="font-bold text-secondary text-xs uppercase">
									Campos de Combustível
								</p>
								<Controller
									control={control}
									name="fuelLiters"
									render={({ field }) => {
										let displayFuelLiters = "";
										if (field.value !== undefined && field.value !== null) {
											const formatted = formatFuelLitersNumber(field.value);
											displayFuelLiters = isFuelLitersFocused
												? formatted
												: `${formatted} L`;
										}

										return (
											<FormFieldControl
												error={errors.fuelLiters?.message}
												htmlFor="expense-fuel-liters"
												isRequired
												label="Quantidade (litros)"
											>
												<Input
													aria-label="Quantidade em litros"
													disabled={isReadOnly || field.disabled}
													id="expense-fuel-liters"
													maxLength={10}
													min={0}
													onBlur={() => {
														setIsFuelLitersFocused(false);
													}}
													onChange={(e) => {
														const v = e.target.value;

														if (v === "") {
															field.onChange(undefined);
															return;
														}

														const num = parseFuelLiters(v);
														if (num === undefined) {
															field.onChange(undefined);
															return;
														}

														field.onChange(num);
													}}
													onFocus={() => {
														setIsFuelLitersFocused(true);
													}}
													placeholder={
														isFuelLitersFocused ? "0,000" : "0,000 L"
													}
													step="0.001"
													type="text"
													value={displayFuelLiters}
												/>
											</FormFieldControl>
										);
									}}
								/>
							</div>
						)}

						{type === EXPENSE_TYPE_CODES.Reimbursement && (
							<div className="flex flex-col gap-3 border-border-secondary border-t pt-4">
								<p className="font-bold text-secondary text-xs uppercase">
									Campos de Reembolso
								</p>
								<Controller
									control={control}
									name="reimbursementReason"
									render={({ field }) => (
										<FormFieldControl
											error={errors.reimbursementReason?.message}
											htmlFor="expense-reimbursement-reason"
											isRequired
											label="Motivo do reembolso"
										>
											<Select
												aria-labelledby="expense-reimbursement-reason"
												className="w-full"
												isDisabled={isReadOnly || field.disabled}
												items={REIMBURSEMENT_REASON_OPTIONS}
												onChange={(v) => {
													field.onChange(Number(v));
												}}
												placeholder="Selecione"
												value={field.value}
											>
												{(item) => (
													<Select.Item id={item.id}>{item.label}</Select.Item>
												)}
											</Select>
										</FormFieldControl>
									)}
								/>
							</div>
						)}

						<Controller
							control={control}
							name="description"
							render={({ field }) => (
								<FormFieldControl
									error={errors.description?.message}
									htmlFor="expense-description"
									isRequired={type === EXPENSE_TYPE_CODES.Other}
									label="Observação"
								>
									<TextAreaBase
										aria-describedby="Observação"
										className="min-h-[80px] resize-y"
										disabled={isReadOnly || field.disabled}
										id="expense-description"
										maxLength={500}
										onChange={(e) => {
											field.onChange(e.target.value);
										}}
										placeholder={
											type === EXPENSE_TYPE_CODES.Other
												? "Informe a observação..."
												: "Detalhes opcionais..."
										}
										value={field.value ?? ""}
									/>
								</FormFieldControl>
							)}
						/>
						{!!expenseData?.hasAttachment && (
							<FormFieldControl htmlFor="fileOld" label="Anexo enviado">
								<button
									aria-label={`Abrir arquivo ${expenseData?.fileName ?? "anexo"}`}
									className={cx(
										"flex w-fit cursor-pointer items-center gap-2 text-brand-secondary text-sm underline-offset-2 hover:underline",
										"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand",
										"disabled:cursor-not-allowed disabled:no-underline disabled:opacity-50"
									)}
									disabled={isOpeningAttachment || !expenseData?.id}
									onClick={() => {
										if (!expenseData?.id) {
											return;
										}
										openAttachment(expenseData.id);
									}}
									type="button"
								>
									<FileAttachment02 className="inline size-3.5 shrink-0 align-middle" />
									{isOpeningAttachment
										? "Abrindo..."
										: (expenseData?.fileName ?? "Abrir arquivo")}
								</button>
							</FormFieldControl>
						)}

						{!isReadOnly && (
							<Controller
								control={control}
								name="attachment"
								render={({ field }) => {
									const file = field.value;
									const isDisabled = !!file?.name;

									return (
										<FormFieldControl
											htmlFor="expense-attachment"
											label="Anexo"
										>
											<FileUpload.Root className="w-full max-w-full">
												<FileUpload.DropZone
													accept=".png, .jpg, .jpeg, .webp, .pdf"
													allowsMultiple={false}
													hint={
														"Envie apenas arquivos PNG, JPG, JPEG, WEBP e PDF.\n" +
														`Tamanho máximo: ${MAX_FILE_SIZE_BYTES / 1024 / 1024} MB`
													}
													isDisabled={isDisabled || field.disabled}
													maxSize={MAX_FILE_SIZE_BYTES}
													onDropFiles={(files) => {
														field?.onChange(files.length > 0 ? files[0] : null);
													}}
													onDropUnacceptedFiles={() =>
														toast(
															<ToastCustom
																description="Arquivo não aceito. Verifique o formato ou tamanho."
																title="Erro"
															/>
														)
													}
													onSizeLimitExceed={() =>
														toast(
															<ToastCustom
																description="Arquivo não aceito. Verifique o formato ou tamanho."
																title="Erro"
															/>
														)
													}
												/>
												{!!file?.name && (
													<FileUpload.List>
														<FileUpload.ListItemProgressBar
															{...file}
															key={file.name}
															name={file.name}
															onDelete={() =>
																field.onChange("attachment", null)
															}
															progress={100}
															size={file.size}
														/>
													</FileUpload.List>
												)}
											</FileUpload.Root>
										</FormFieldControl>
									);
								}}
							/>
						)}

						{mode !== "view" && (
							<Button
								aria-label={
									mode === "edit" ? "Salvar alterações" : "Registrar despesa"
								}
								className="mt-2 gap-2"
								disabled={isSubmitting || isPending || disabled}
								onKeyDown={handleSubmitKeyDown}
								tabIndex={0}
								type="submit"
							>
								{isPending ? <Spinner disabled /> : <Plus className="size-4" />}
								{mode === "edit" ? "Salvar" : "Registrar despesa"}
							</Button>
						)}
					</form>
				</div>
			</Modal>
		</ModalOverlay>
	);
}
